// When the job ran, what it was called, and why it ended.
//
// The reason is a fixed list because a recruiter has to be able to filter on
// it, with Other revealing free text so the list never blocks the truth. One
// value carries a consequence: choosing "Terminated / discharged" answers the
// termination certification, which is why it has to stay an explicit option
// rather than hiding inside Other.

import { terminationFromReason } from '../lib/certifications'
import type { Errors } from '../lib/validation'
import { JOB_TITLES, REASONS_FOR_LEAVING } from '../values'

import { MonthField } from './MonthField'
import { SectionHeader } from './SectionHeader'
import { TitleChips } from './TitleChips'

export interface DetailsSectionProps {
  startMonth: number | null
  endMonth: number | null
  isCurrent: boolean
  position: string | null
  reasonForLeaving: string | null
  onChange: (
    patch: Partial<{
      startMonth: number | null
      endMonth: number | null
      isCurrent: boolean
      position: string | null
      reasonForLeaving: string | null
      isDriverTerminated: boolean | null
    }>
  ) => void
  errors?: Errors
  maxMonth?: number
  /** Outstanding field count, shown beside the heading. */
  outstanding?: number
}

export const DetailsSection = ({
  startMonth,
  endMonth,
  isCurrent,
  position,
  reasonForLeaving,
  onChange,
  errors = {},
  maxMonth,
  outstanding = 0,
}: DetailsSectionProps) => {
  const chosenFromList =
    reasonForLeaving != null &&
    (REASONS_FOR_LEAVING as readonly string[]).includes(reasonForLeaving)
  const isOther = reasonForLeaving != null && !chosenFromList

  return (
    <div className="rounded-[13px] border border-doubleNickel-gray-200 bg-white px-3 py-2.5">
      <SectionHeader
        title="Details"
        count={outstanding ? `${outstanding} left` : undefined}
      />

      <div className="mt-2 flex gap-2.5">
        <div className="min-w-0 flex-1">
          <MonthField
            label="Start"
            required
            value={startMonth}
            maxMonth={maxMonth}
            error={errors.startMonth}
            onChange={(value) => onChange({ startMonth: value })}
          />
        </div>
        <div className="min-w-0 flex-1">
          {isCurrent ? (
            <div>
              {/*
                Matches MonthField's label and input metrics exactly, or Present
                sits a few pixels above the date beside it.
              */}
              <div className="mb-1 text-[11px] font-medium text-doubleNickel-gray-600">
                End
              </div>
              <div className="flex items-center border-b border-doubleNickel-gray-300">
                <span className="w-full py-1.5 text-[15px] text-doubleNickel-gray-900">
                  Present
                </span>
              </div>
            </div>
          ) : (
            <MonthField
              label="End"
              required
              value={endMonth}
              maxMonth={maxMonth}
              // A job cannot end before it began; the field refuses it rather
              // than letting Save be the first place anyone says so
              minMonth={startMonth ?? undefined}
              error={errors.endMonth}
              onChange={(value) => onChange({ endMonth: value })}
            />
          )}
        </div>
      </div>

      <label className="mt-2 flex items-center gap-2 text-[11.5px] text-doubleNickel-gray-700">
        <input
          type="checkbox"
          checked={isCurrent}
          onChange={(event) =>
            onChange(
              event.target.checked
                ? {
                    isCurrent: true,
                    endMonth: null,
                    reasonForLeaving: null,
                    // Entailed by still working there
                    isDriverTerminated: false,
                  }
                : // Unchecking takes the entailment back with it. Leaving false
                  // behind would keep an answer the driver never gave.
                  { isCurrent: false, isDriverTerminated: null }
            )
          }
          className="h-3.5 w-3.5 accent-doubleNickel-brand-600"
        />
        I still work here
      </label>

      {!isCurrent && (
        <div className="mt-2.5">
          <div className="mb-0.5 text-[10px] font-medium text-doubleNickel-gray-600">
            Reason for Leaving
            <span className="ml-0.5 text-doubleNickel-error-600">*</span>
          </div>
          <select
            aria-label="Reason for Leaving"
            value={isOther ? 'Other' : reasonForLeaving ?? ''}
            onChange={(event) => {
              const next = event.target.value
              if (next === '') return onChange({ reasonForLeaving: null })
              // "Other" clears the value so the free field starts empty rather
              // than holding a list value the driver did not choose
              const reason = next === 'Other' ? '' : next
              const terminated = terminationFromReason(reason)
              onChange(
                terminated === undefined
                  ? { reasonForLeaving: reason }
                  : { reasonForLeaving: reason, isDriverTerminated: terminated }
              )
            }}
            className="w-full border-b border-doubleNickel-gray-300 bg-transparent py-1 text-[12.5px] text-doubleNickel-gray-900 outline-none"
          >
            <option value="">Select…</option>
            {REASONS_FOR_LEAVING.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
            <option value="Other">Other…</option>
          </select>
          {isOther && (
            <input
              type="text"
              aria-label="Reason for Leaving — other"
              value={reasonForLeaving}
              placeholder="Brief explanation"
              onChange={(event) =>
                onChange({ reasonForLeaving: event.target.value })
              }
              className="mt-2 w-full border-b border-doubleNickel-gray-300 bg-transparent py-1 text-[12.5px] text-doubleNickel-gray-900 outline-none placeholder:text-doubleNickel-gray-400"
            />
          )}
          {errors.reasonForLeaving && (
            <div className="mt-1 text-[10px] text-doubleNickel-error-700">
              {errors.reasonForLeaving}
            </div>
          )}
        </div>
      )}

      <div className="mt-2.5">
        <TitleChips
          label="Job Title"
          required
          options={JOB_TITLES}
          value={position}
          error={errors.position}
          onChange={(value) => onChange({ position: value })}
        />
      </div>
    </div>
  )
}
